import { useCallback, useState } from "react";
import { Sparkles } from "lucide-react";
import { Kbd } from "~/components/Kbd";
import { Button } from "~/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { Tooltip, TooltipContent, TooltipTrigger } from "~/components/ui/tooltip";
import { useModShortcut } from "~/hooks/useModShortcut";
import { modShortcutText } from "~/lib/keyboard-shortcuts";
import { cn } from "~/lib/utils";
import { PlatformChatPanel } from "~/pages/manifold-nextgen/components/platform-help/PlatformChatPanel";
import { useCanUseAIAgents } from "~/pages/manifold-nextgen/hooks/useCanUseAIAgents";

interface ManifoldNextGenHelpPopoverProps {
  // See ManifoldNextGenNavPickerProps["colored"] — same colored-home-hero treatment.
  colored?: boolean;
}

export function ManifoldNextGenHelpPopover({ colored = false }: ManifoldNextGenHelpPopoverProps) {
  const [open, setOpen] = useState(false);
  const enabled = useCanUseAIAgents();
  const toggleOpen = useCallback(() => setOpen((current) => !current), []);
  useModShortcut("k", toggleOpen, enabled);
  const shortcut = modShortcutText("k");

  if (!enabled) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              aria-label="Ask AI"
              aria-keyshortcuts={shortcut.aria}
              className={cn(
                "size-9 rounded-full p-0 text-muted-foreground duration-300 hover:bg-black/[0.06] hover:text-foreground focus-visible:ring-0",
                colored && "text-white/80 hover:bg-white/20 hover:text-white",
                open && (colored ? "bg-white/20 text-white" : "bg-black/[0.06] text-foreground")
              )}
            >
              <Sparkles className="size-5" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        {/* Hidden while open, so it doesn't cover the panel. */}
        {!open && (
          <TooltipContent side="bottom" className="flex items-center gap-2">
            Ask AI
            <Kbd>{shortcut.label}</Kbd>
          </TooltipContent>
        )}
      </Tooltip>
      <PopoverContent
        align="end"
        sideOffset={10}
        // Focus the panel unless it already focused its composer: its first
        // control would open a tooltip, and focus on the trigger would Tab out.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const panel = event.currentTarget as HTMLElement;
          if (!panel.contains(document.activeElement)) {
            panel.focus();
          }
        }}
        className="relative z-[1300] w-[26rem] overflow-visible p-0 shadow-2xl"
      >
        {/* The arrow, centered under the trigger. */}
        <div
          aria-hidden
          className="absolute -top-1.5 right-3 size-3 rotate-45 rounded-[2px] border-t border-l border-border bg-popover"
        />
        <PlatformChatPanel />
      </PopoverContent>
    </Popover>
  );
}
